// The 10 BREAKPOINT trading strategies.
// `desc` = one-line summary shown on the card. `fullDesc` = the FULL, exact decision logic
// (thresholds, tiers, filters and trade windows) mirrored from the backend engine
// (trading_strategies.py) so users see precisely how each bet is decided.
export const STRATEGY_INFO = {
  "Match Point Failure Strategy": {
    desc: "Backs the player who held the match's first match point, in set 2 or 3.",
    risk: "Medium Risk",
    fullDesc:
`STRATEGY 1 — Match Point Failure

WHAT IT DOES
Backs the player who reached the FIRST match point of the match (not the player who saved it).

EXACT LOGIC
1. The first match point of the match is detected → that player is stored as the trigger.
2. The bet is only placed while the match is in the 2nd OR 3rd set.
3. The trigger player's LIVE decimal odds must be between 1.30 and 2.50.
4. If the match goes past the 3rd set, the trigger is cancelled.

LIMITS
• Max 1 trade per match.
• Odds window: 1.30x – 2.50x.`,
  },
  "Both Players Match Point Strategy": {
    desc: "Doubles only: in a 3rd-set super tie-break, backs the team to come back from match point.",
    risk: "High Risk",
    fullDesc:
`STRATEGY 2 — Doubles Super Tie-Break Counter

WHAT IT DOES
A comeback bet that ONLY applies to DOUBLES matches in a 3rd-set super tie-break (singles are skipped entirely).

EXACT LOGIC
1. Must be a doubles match (player names contain "/").
2. Sets must be 1-1 and the match in the deciding 3rd-set super tie-break.
3. When one team reaches match point (≥ 9 points) AND the points gap is ≤ 2
   (e.g. 9-7, 9-8, 10-9) → BACK THE OPPONENT (the team NOT on match point).
4. Requires valid live odds for the opponent.

LIMITS
• Max 1 trade per match.
• Max points gap to trigger: 2.`,
  },
  "Instant Match Point Strategy": {
    desc: "Instantly market-buys ANY player the moment they reach match point.",
    risk: "High Risk",
    fullDesc:
`STRATEGY 3 — Instant Match Point

WHAT IT DOES
The moment ANY player reaches match point, immediately place a MARKET (fill-or-kill) bet on them. Speed over price.

EXACT LOGIC
1. Match point detected for a player → instant MARKET BUY.
2. Requires real live odds (skips if no Cloudbet market is found).
3. Minimum odds floor: 1.05x (lower than other strategies).
4. Up to 5% slippage allowed for an instant fill.

LIMITS
• Each player bet at most once per match.`,
  },
  "First Set Winner Strategy": {
    desc: "Backs the set-1 winner early in set 2 — with a dead-zone skip, odds-flip and underdog filter.",
    risk: "Medium Risk",
    fullDesc:
`STRATEGY 4 — First Set Winner

WHAT IT DOES
Backs the player who won the first set, placed early in the second set — but only at sensible prices.

TRADE WINDOW
• First set complete (6+ games with a 2+ lead, or 7-6 tie-break).
• Fewer than 3 games completed in the 2nd set.
• Minimum odds: 1.10x (skips anything shorter).

3-TIER ODDS LOGIC (live decimal odds of the set-1 winner)
• 1.10x – 1.50x  → BACK the set-1 winner (normal).
• 1.50x – 1.85x  → DEAD ZONE, skip the match entirely.
• Above 1.85x    → FLIP: back the set-1 LOSER instead.

PRE-MATCH UNDERDOG FILTER
• If the set-1 winner's PRE-MATCH odds were ≥ 3.83x (a heavy underdog who got lucky),
  back the OPPONENT (the pre-match favorite) instead.

LIMITS
• Max 1 trade per match, max $10 per match.`,
  },
  "Odds Reversal Strategy": {
    desc: "Backs a 100%-dominant favorite the moment odds flip against them in the 3rd set.",
    risk: "Medium-High Risk",
    fullDesc:
`STRATEGY 5 — Dominant Favorite Odds Reversal

WHAT IT DOES
Backs a player who was THE favorite for the entire match, the instant the odds reverse against them deep in the decider — betting on a momentum recovery.

EXACT LOGIC
1. After every completed game, an odds snapshot is recorded.
2. Need at least 10 snapshots, and the SAME player must be favorite in ALL of them (100% dominance).
3. The match must be in the 3rd set with 6+ games completed.
4. The current favorite must have FLIPPED to the other player (a reversal occurred).
5. The new favorite's odds must be ≥ 1.70x — i.e. not too heavily favored (so the reversal isn't already decisive).
6. → BACK the original dominant favorite.

LIMITS
• Max 1 trade per match (can run alongside other strategies on the same match).`,
  },
  "Straight Sets Strategy": {
    desc: 'Pre-match: backs "match ends in exactly 2 sets" when priced 1.25–1.58.',
    risk: "Medium Risk",
    fullDesc:
`STRATEGY 6 — Straight Sets (Pre-Match)

WHAT IT DOES
A pre-match bet that the match finishes in exactly two sets (a straight-sets win).

EXACT LOGIC
1. Scans Cloudbet pre-match tennis events (runs on a 5-minute cycle).
2. Reads the "exact number of sets = 2" market (tennis.exact_sets/outcome=2).
3. Places the bet only if the decimal odds are between 1.25x and 1.58x.

LIMITS
• Max 1 trade per match. Pre-match only.`,
  },
  "Set Equalizer Strategy": {
    desc: "When sets go 1-1, backs the set-1 winner to recover — with dead-zone, flip and underdog rules.",
    risk: "Medium Risk",
    fullDesc:
`STRATEGY 7 — Set Equalizer (Bounce Back)

WHAT IT DOES
When the opponent levels the match at one set all, backs the set-1 winner to recover and take the decider (their odds have drifted, creating value).

TRADE WINDOW
• Set 1 complete, then the OTHER player wins Set 2 → sets are 1-1.
• Placed in the first 3 games of the deciding 3rd set.

ODDS LOGIC (live decimal odds of the set-1 winner)
• 1.19x – 1.86x  → BACK the set-1 winner.
• 1.87x – 2.20x  → DEAD ZONE, skip the match permanently.
• Above 2.20x    → FLIP: back the set-2 winner instead, but ONLY if their odds are 1.30x – 1.70x.

PRE-MATCH UNDERDOG FILTER
• If the set-1 winner's PRE-MATCH odds were ≥ 3.83x, back the set-2 winner instead.

LIMITS
• Max 1 trade per match.`,
  },
  "Home Advantage Strategy": {
    desc: "Backs a player competing in their home country (or the home underdog if both are local).",
    risk: "Low-Medium Risk",
    fullDesc:
`STRATEGY 8 — Home Advantage

WHAT IT DOES
Backs a player who is from the same country as the tournament host, capturing the measurable edge of home-crowd support.

EXACT LOGIC
1. Detects whether a player is local to the tournament's host country.
2. If ONE player is local → BACK that player (max odds 3.00x).
3. If BOTH players are local → BACK the underdog/higher-odds player (max odds 4.00x).
4. Minimum odds: 1.10x. Triggered early, before Set 1 completes.

LIMITS
• Max 1 trade per match.`,
  },
  "Pre-Match Favorite Strategy": {
    desc: "Backs the pre-match favorite on every eligible event priced 1.20–1.59.",
    risk: "Low Risk",
    fullDesc:
`STRATEGY 9 — Pre-Match Favorite

WHAT IT DOES
Backs the favorite (the lower-odds player) in the match-winner market across eligible tennis events — singles and doubles.

EXACT LOGIC
1. Scans all Cloudbet tennis events (pre-match and early live).
2. Identifies the favorite (lower odds) of the two players.
3. Backs that favorite only if their odds are between 1.20x and 1.59x
   (skips near-certainties below 1.20x and anything above 1.59x that isn't a clear favorite).

LIMITS
• Only 1 confirmed bet per event.`,
  },
  "Pre-Match Multi-Bet Strategy": {
    desc: "Combines 2–3 very heavy favorites (each 1.05–1.15) into one parlay.",
    risk: "Medium Risk",
    fullDesc:
`STRATEGY 10 — Multi-Bet / Bet Builder (Parlay)

WHAT IT DOES
Combines several very heavy favorites into a single parlay for stronger combined odds at lower individual risk.

EXACT LOGIC
1. Scans all Cloudbet tennis events (pre-match or very early live, score 0-0).
2. Selects matches where the favorite's odds are between 1.05x and 1.15x.
3. Combines 2 to 3 qualifying legs into one multi-bet (combined odds = product of the legs).
4. ATP / WTA / Challenger events only (no ITF).

LIMITS
• 2–3 legs per parlay. A player can appear in at most 3 different multi-bets.`,
  },
  "Set Handicap Strategy": {
    desc: "You pick the matches. Backs your player at +1.5 sets (Bo3) / +2.5 (Bo5) — win 1 set, win the bet.",
    risk: "Low–Medium Risk",
    fullDesc:
`STRATEGY 11 — Set Handicap (manual match selection)

WHAT IT DOES
This is the only strategy where YOU choose the matches. From the "Set Handicap" tab you pick a
pre-match match and select which player is "your player". The engine then backs that player on the
SET HANDICAP market only — it never touches the Moneyline / Match-Winner market.

WHY SET HANDICAP +1.5 / +2.5
A set handicap adds virtual sets to your player's final set score:
• Best-of-3 match → +1.5 sets. Your player only has to win 1 set (e.g. lose 1-2 → 2.5-2, bet wins).
• Best-of-5 match → +2.5 sets. Again your player only has to win 1 set to win the bet.
So in BOTH formats the bet is won the moment your player wins a single set — a much safer line than
backing them to win the match outright, usually at attractive odds.

EXACT LOGIC
1. Bet 1 is placed PRE-MATCH, or while set 1 is in progress — but ONLY until 4 games of set 1 have
   been completed. After that the match is skipped (status "Missed").
2. If your player WINS any set → the handicap is secured → the match is marked Won and removed.
3. If your player LOSES set 1 → the engine re-backs them ONCE more at the current (now better-value)
   +handicap odds. Maximum 2 bets per match.
4. If your player wins 0 sets and the opponent clinches the match → marked Lost.

TOURNAMENTS
• Only Grand Slam, ATP, WTA, WTA-125 and Challenger matches are shown for selection — ITF, UTR and
  other lower-tier events are excluded, exactly like every other strategy.

NOTES
• Any odds accepted, as long as they're above 1.10x.
• Stake = this strategy's Trade Amount; the same stake is used for both bets.
• Runs fully independently — your other strategies keep working exactly as before.
• Enable this strategy here, turn on Automated Trading, then add/track matches in the "Set Handicap" tab.`,
  },
  "Match Winner Strategy": {
    desc: "You pick the matches + the winner. Instantly backs your player on the Match-Winner market, with an optional DCA 2nd bet if they lose set 1.",
    risk: "Medium Risk",
    fullDesc:
`STRATEGY 12 — Match Winner (manual match selection)

WHAT IT DOES
You choose the matches. From the "Match Winners" tab you pick a match and select which player will
WIN it. The engine instantly backs that player on the MONEYLINE / MATCH-WINNER market
(tennis.winner) at the current live odds.

EXACT LOGIC
1. Bet 1 is placed pre-match, or while set 1 is in progress — but ONLY until 4 games of set 1 have
   been completed. After that the match is skipped (status "Missed").
2. If your player WINS the match → marked Won. If they LOSE the match → marked Lost.

DCA (optional 2nd bet — toggle in this card)
• When DCA is ON and your backed player LOSES SET 1, the engine places a SECOND bet on the SAME
  player at the new (higher) live match-winner odds, during the set-1 layover.
• Exactly ONE DCA bet per match (even in a best-of-5). The DCA stake is a SEPARATE amount you set
  here; leave it blank to reuse your first-bet stake.

TOURNAMENTS
• Only Grand Slam, ATP, WTA, WTA-125 and Challenger matches are shown for selection — ITF, UTR and
  other lower-tier events are excluded, exactly like every other strategy.

NOTES
• Any live odds accepted (above 1.01x).
• Runs fully independently — your other strategies keep working exactly as before.
• Enable this strategy here, turn on Automated Trading, then add/track matches in the "Match Winners" tab.`,
  },
};
